import {
  UPLOAD_RESET,
  UPLOAD_START,
  UPLOAD_COMPLETE,
  UPLOAD_ITEM_PROGRESS,
  UPLOAD_ITEM_COMPLETE,
  UPLOAD_ITEM_FAIL,
  UPLOAD_CANCEL_REQUEST,
  UPLOAD_FAIL,
  UPLOAD_ITEM_PART_PROGRESS,
} from './fileUploadActions';

export const initialState = {
  files: [],
  inProgress: [],
  completed: [],
  failed: [],
  uploading: false,
  complete: false,
  current: 0,
  canceled: false,
  cancelToken: undefined,
};

/**
 * @typedef UploadItem
 * @property {File} file The file to be uploaded.
 * @property {Date} startDate Time the file started at.
 * @property {Event} progressEvent The progress event of the file. Only available when the item is in Progress.
 * @property {Error} error The error of a failed item. Only available if the item has failed to upload.
 */

const fileUploadReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case UPLOAD_RESET:
      return {
        ...initialState,
      };
    case UPLOAD_START:
      return {
        ...initialState,
        uploading: true,
        files: action.files,
        cancelToken: action.cancelToken,
      };

    case UPLOAD_ITEM_PROGRESS: {
      // After canceling some photos may end up here
      // due to async nature. Ignore those for good.
      if (state.canceled) {
        return state;
      }

      const isFileInProgress = state.inProgress.find(
        uploadItem => uploadItem.file.name === action.uploadItem.file.name,
      );

      return {
        ...state,
        inProgress: isFileInProgress
          ? state.inProgress.map(uploadItem =>
              uploadItem.file.name === action.uploadItem.file.name
                ? action.uploadItem
                : uploadItem,
            )
          : [action.uploadItem, ...state.inProgress],
      };
    }
    case UPLOAD_ITEM_PART_PROGRESS: {
      // After canceling some photos may end up here
      // due to async nature. Ignore those for good.
      if (state.canceled) {
        return state;
      }

      const fileInProgress = state.inProgress.find(
        uploadItem => uploadItem.file.name === action.uploadPartItem.filename,
      );

      if (!fileInProgress) {
        return state;
      }

      const partsProgressEvents = fileInProgress.partsProgressEvents.map(
        partProgressEvent => {
          if (partProgressEvent.id === action.uploadPartItem.id) {
            return {
              ...partProgressEvent,
              progressEvent: action.uploadPartItem.progressEvent,
            };
          }

          return partProgressEvent;
        },
      );

      const progressEvent = new ProgressEvent('progress', {
        total: fileInProgress.file.size,
        loaded: partsProgressEvents.reduce(
          (sum, partProgressEvent) =>
            sum + partProgressEvent.progressEvent.loaded,
          0,
        ),
      });

      return {
        ...state,
        inProgress: state.inProgress.map(uploadItem =>
          uploadItem.file.name === action.uploadPartItem.filename
            ? {
                ...fileInProgress,
                progressEvent,
                partsProgressEvents,
              }
            : uploadItem,
        ),
      };
    }
    case UPLOAD_ITEM_COMPLETE:
      return {
        ...state,
        inProgress: state.inProgress.filter(
          uploadItem => uploadItem.file.name !== action.uploadItem.file.name,
        ),
        completed: [action.uploadItem, ...state.completed],
        current: state.current + 1,
      };
    case UPLOAD_ITEM_FAIL:
      // After canceling some photos may end up here
      // due to async nature. Ignore those for good.
      if (state.canceled) {
        return state;
      }

      return {
        ...state,
        inProgress: state.inProgress.filter(
          uploadItem => uploadItem.file.name !== action.uploadItem.file.name,
        ),
        failed: [action.uploadItem, ...state.failed],
        current: state.current + 1,
      };

    case UPLOAD_COMPLETE:
      return {
        ...state,
        uploading: false,
        complete: true,
        current: 0,
      };

    case UPLOAD_FAIL:
      return {
        ...state,
        failed: state.files.map(file => ({ file, startDate: new Date() })),
        uploading: false,
        complete: true,
        current: 0,
      };

    case UPLOAD_CANCEL_REQUEST: {
      // Attempt to cancel the request from the state:
      state.cancelToken?.cancel('Upload canceled by the user');

      return {
        ...state,
        canceled: true,
        // Clean in progress / failed photos.
        // We can keep succeeded ones.
        uploading: false,
        inProgress: [],
        failed: [],
        files: [],
      };
    }

    default:
      return state;
  }
};

export default fileUploadReducer;
